import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Linkedin, Send, Globe } from "lucide-react";
import { PROFILE, SERVICES } from "@/data";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["< $100", "$100 – $500", "$500 – $2k", "$2k+"];

const CHANNELS = [
  { label: "WhatsApp", value: "+880 1722 515774", href: PROFILE.whatsapp, icon: MessageCircle, testid: "link-social-whatsapp" },
  { label: "Email", value: PROFILE.email, href: `mailto:${PROFILE.email}`, icon: Mail, testid: "link-social-email" },
  { label: "LinkedIn", value: "linkedin.com/in/iamnurul", href: PROFILE.linkedin, icon: Linkedin, testid: "link-social-linkedin" },
  { label: "Website", value: "imnurul.com", href: PROFILE.website, icon: Globe, testid: "link-social-website" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [services, setServices] = useState([]);
  const [budget, setBudget] = useState("");
  const [sending, setSending] = useState(false);
  const [dhakaTime, setDhakaTime] = useState("");

  useEffect(() => {
    const tick = () =>
      setDhakaTime(new Date().toLocaleTimeString("en-US", { timeZone: "Asia/Dhaka", hour: "2-digit", minute: "2-digit" }));
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, services, budget });
      toast.success("Enquiry sent — Sumon will reply within 24 hours");
      setForm({ name: "", email: "", message: "" });
      setServices([]);
      setBudget("");
    } catch {
      toast.error("Could not send — try WhatsApp instead");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] rounded-full bg-[#D9A441]/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionHeading id="contact" overline="Contact / 06" title="Let's build something that sells" />
          <Reveal className="space-y-4">
            {CHANNELS.map((c) => (
              <a
                key={c.label}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={c.testid}
                className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-[#101F38]/70 p-5 hover:border-[#D9A441]/50 transition-all duration-300 hover:scale-[1.02]"
              >
                <span className="w-12 h-12 rounded-xl bg-[#D9A441]/10 border border-[#D9A441]/25 flex items-center justify-center text-[#D9A441]">
                  <c.icon size={20} />
                </span>
                <span>
                  <span className="block font-display font-semibold text-[#F3EFE6]">{c.label}</span>
                  <span className="block text-sm text-[#9DAEC4]">{c.value}</span>
                </span>
              </a>
            ))}
            <div className="flex items-center gap-3 rounded-2xl border border-[#D9A441]/30 bg-[#D9A441]/5 p-5" data-testid="timezone-widget">
              <Globe size={18} className="text-[#D9A441]" />
              <p className="text-sm text-[#C8D3E0]">
                Bogura, Bangladesh (GMT+6) · <span className="font-mono text-[#D9A441]">{dhakaTime}</span> · Accepting global projects
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="form-contact-enquiry" className="rounded-2xl border border-white/10 bg-[#101F38] shadow-xl shadow-black/30 p-7 sm:p-9 space-y-6">
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="input-name"
                  className="w-full bg-[#0A1730] border border-white/15 rounded-xl px-4 py-3.5 text-[#F3EFE6] placeholder:text-[#5B6B7E] focus:outline-none focus:border-[#D9A441]/70 transition-colors"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email"
                  data-testid="input-email"
                  className="w-full bg-[#0A1730] border border-white/15 rounded-xl px-4 py-3.5 text-[#F3EFE6] placeholder:text-[#5B6B7E] focus:outline-none focus:border-[#D9A441]/70 transition-colors"
                />
              </div>

              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-[#9DAEC4] mb-3">I need help with</p>
                <div className="flex flex-wrap gap-2.5">
                  {SERVICES.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => toggleService(s.title)}
                      data-testid={`chip-service-${s.id}`}
                      className={`text-xs font-mono uppercase tracking-widest rounded-full px-4 py-2 border transition-all duration-300 ${
                        services.includes(s.title)
                          ? "border-[#D9A441]/60 text-[#D9A441] bg-[#D9A441]/10"
                          : "border-white/15 text-[#9DAEC4] hover:border-white/30"
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-[#9DAEC4] mb-3">Estimated budget</p>
                <div className="flex flex-wrap gap-2.5">
                  {BUDGETS.map((b) => (
                    <button
                      type="button"
                      key={b}
                      onClick={() => setBudget(b)}
                      data-testid={`chip-budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`text-xs font-mono rounded-full px-4 py-2 border transition-all duration-300 ${
                        budget === b
                          ? "border-[#D9A441]/60 text-[#D9A441] bg-[#D9A441]/10"
                          : "border-white/15 text-[#9DAEC4] hover:border-white/30"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell me about your project..."
                rows={4}
                data-testid="input-message"
                className="w-full bg-[#0A1730] border border-white/15 rounded-xl px-4 py-3.5 text-[#F3EFE6] placeholder:text-[#5B6B7E] focus:outline-none focus:border-[#D9A441]/70 transition-colors resize-none"
              />

              <button
                type="submit"
                disabled={sending}
                data-testid="btn-submit-enquiry"
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#D9A441] hover:bg-[#E5B85C] disabled:opacity-50 text-[#0A1730] font-bold px-8 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.03]"
              >
                <Send size={16} />
                {sending ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
